/**
 * useEmbedMessaging — postMessage bridge between an embedded Thread iframe
 * and its parent window.
 *
 * Inbound commands (parent → embed) are dispatched to callbacks.
 * Outbound events (embed → parent) are sent via `sendEvent`.
 */

import { useEffect, useCallback, useRef } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CheckoutAction {
  name: "checkout";
  branch: string;
}

export interface SetReferenceAction {
  name: "set_reference";
  parameter: string;
  value: number;
  units?: string;
}

export interface FocusNodeAction {
  name: "focus_node";
  path: string;
}

export interface ResetAction {
  name: "reset";
}

export interface SetThemeAction {
  name: "set_theme";
  theme: "light" | "dark";
}

export type ThreadEmbedAction =
  | CheckoutAction
  | SetReferenceAction
  | FocusNodeAction
  | ResetAction
  | SetThemeAction;

export interface ThreadEmbedCommand {
  type: "thread:command";
  action: ThreadEmbedAction;
}

// -- Outbound events --

export interface ReadyEvent {
  name: "ready";
}

export interface NodeClickedEvent {
  name: "node_clicked";
  path: string;
  nodeId: string;
}

export interface ReferenceChangedEvent {
  name: "reference_changed";
  parameter: string;
  oldValue: number;
  newValue: number;
}

export interface BranchChangedEvent {
  name: "branch_changed";
  branch: string;
}

export interface MergeCompleteEvent {
  name: "merge_complete";
  source: string;
  target: string;
}

export type ThreadEmbedEventPayload =
  | ReadyEvent
  | NodeClickedEvent
  | ReferenceChangedEvent
  | BranchChangedEvent
  | MergeCompleteEvent;

export interface ThreadEmbedEvent {
  type: "thread:event";
  event: ThreadEmbedEventPayload;
}

// -- Hook options --

export interface UseEmbedMessagingOptions {
  /** Called when parent sends a checkout command. */
  onCheckout?: (branch: string) => void;
  /** Called when parent sends a set_reference command. */
  onSetReference?: (parameter: string, value: number, units?: string) => void;
  /** Called when parent sends a focus_node command. */
  onFocusNode?: (path: string) => void;
  /** Called when parent sends a reset command. */
  onReset?: () => void;
  /** Called when parent sends a set_theme command. */
  onSetTheme?: (theme: "light" | "dark") => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEmbedMessaging(options: UseEmbedMessagingOptions = {}) {
  // Store callbacks in a ref so the message listener always sees the latest
  // without needing to re-register.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const data = event.data;
      if (!data || typeof data !== "object" || data.type !== "thread:command") {
        return;
      }

      const command = data as ThreadEmbedCommand;
      const { action } = command;

      switch (action.name) {
        case "checkout":
          optionsRef.current.onCheckout?.(action.branch);
          break;
        case "set_reference":
          optionsRef.current.onSetReference?.(action.parameter, action.value, action.units);
          break;
        case "focus_node":
          optionsRef.current.onFocusNode?.(action.path);
          break;
        case "reset":
          optionsRef.current.onReset?.();
          break;
        case "set_theme":
          optionsRef.current.onSetTheme?.(action.theme);
          break;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const sendEvent = useCallback((event: ThreadEmbedEventPayload) => {
    if (window.parent === window) return; // not in an iframe
    const message: ThreadEmbedEvent = { type: "thread:event", event };
    window.parent.postMessage(message, "*");
  }, []);

  return { sendEvent };
}
